/**
 * What the crafting state says about one placed structure: its name, its
 * class, the slots its jobs hold and when the soonest running one lands.
 *
 * Pure over the state it is handed, so the hover label and the beacon read one
 * answer and a test can pin it without a crafting module in the room.
 */

import type { ActiveJob, CraftBuilding } from '../crafting';

export type StructureMode = 'idle' | 'active' | 'paused' | 'ready';

export interface StructureStatus {
  name: string;
  /** The class as a player reads it: the building def's name, or the kind. */
  className: string;
  category: string | null;
  slotsUsed: number;
  slotsMax: number;
  mode: StructureMode;
  /** The recipe the soonest running job is making, when one runs. */
  jobName: string | null;
  /** Milliseconds until that job lands, when one runs. */
  etaMs: number | null;
  pauseReason: string | null;
}

export interface StructureRef {
  kind: string;
  cell: string;
  buildingId?: string;
}

export interface StatusSources {
  buildings: readonly CraftBuilding[];
  jobs: readonly ActiveJob[];
  defs: Readonly<Record<string, { name: string; maxSlots: number; category?: string }>>;
  recipeName(id: string): string | undefined;
  readyAt(job: ActiveJob): number;
  isReady(job: ActiveJob, now: number): boolean;
  /** The slots a building has at its current level; absent reads the class's level-1 count. */
  slotsOf?(b: CraftBuilding): number;
}

/** The solar array has no BLDG# row and no slots; its name is the kind's own. */
const ARRAY_NAME = 'Photovoltaic array';

function titleCase(kind: string): string {
  return kind.replace(/[_-]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/** The crafting building a placement stands for: its own row id, else its cell and class. */
export function buildingFor(p: StructureRef, buildings: readonly CraftBuilding[]): CraftBuilding | null {
  if (p.buildingId) {
    const byId = buildings.find((b) => b.id === p.buildingId);
    if (byId) return byId;
  }
  return buildings.find((b) => b.hexId === p.cell && b.buildingClass === p.kind) ?? null;
}

export function structureStatus(p: StructureRef, src: StatusSources, now: number): StructureStatus {
  const def = src.defs[p.kind];
  // An H3 id ends in its unused digits, `f` padding, so the tail that tells
  // two cells apart sits before them.
  const shortCell = p.cell.replace(/f+$/, '').slice(-5);
  if (p.kind === 'solar') {
    return {
      name: `${ARRAY_NAME} ${shortCell}`, className: 'Solar array', category: 'power',
      slotsUsed: 0, slotsMax: 0, mode: 'idle', jobName: null, etaMs: null, pauseReason: null,
    };
  }
  const b = buildingFor(p, src.buildings);
  const jobs = b ? src.jobs.filter((j) => j.buildingId === b.id && !j.claimed) : [];
  let mode: StructureMode = 'idle';
  let soonest: ActiveJob | null = null;
  let paused: ActiveJob | null = null;
  let ready = false;
  for (const j of jobs) {
    if (src.isReady(j, now)) { ready = true; continue; }
    if ((j.pausedAt ?? 0) > 0) { paused ??= j; continue; }
    if (!soonest || src.readyAt(j) < src.readyAt(soonest)) soonest = j;
  }
  if (soonest) mode = 'active';
  else if (paused) mode = 'paused';
  else if (ready) mode = 'ready';
  const shown = soonest ?? paused;
  return {
    name: b?.name ?? `${def?.name ?? titleCase(p.kind)} ${shortCell}`,
    className: def?.name ?? titleCase(p.kind),
    category: def?.category ?? null,
    slotsUsed: jobs.length,
    slotsMax: b && src.slotsOf ? src.slotsOf(b) : def?.maxSlots ?? 0,
    mode,
    jobName: shown ? src.recipeName(shown.recipeId) ?? shown.recipeId : null,
    etaMs: soonest ? Math.max(0, src.readyAt(soonest) - now) : null,
    pauseReason: !soonest && paused ? paused.pauseReason ?? 'no power' : null,
  };
}

export interface CellBuilding {
  /** The BLDG# row, for a jump into the Forge; null for an array or a placement with no row. */
  buildingId: string | null;
  kind: string;
  level: number | null;
  /** The name the card heads the row with: the row's own, else the class's; the hex is the card's, so no cell tail. */
  name: string;
  /** Jobs landed and waiting for a claim. */
  readyJobs: number;
  status: StructureStatus;
}

/** Every placement on `cell`, read off the crafting state: buildings by name, then arrays. */
export function buildingsOnCell(
  cell: string, placements: readonly StructureRef[], src: StatusSources, now: number,
): CellBuilding[] {
  return placements
    .filter((p) => p.cell === cell)
    .map((p) => {
      const b = p.kind === 'solar' ? null : buildingFor(p, src.buildings);
      const readyJobs = b ? src.jobs.filter((j) => j.buildingId === b.id && !j.claimed && src.isReady(j, now)).length : 0;
      const name = b?.name ?? (p.kind === 'solar' ? ARRAY_NAME : src.defs[p.kind]?.name ?? titleCase(p.kind));
      return { buildingId: b?.id ?? null, kind: p.kind, level: b?.level ?? null, name, readyJobs, status: structureStatus(p, src, now) };
    })
    .sort((a, b) => Number(a.kind === 'solar') - Number(b.kind === 'solar') || a.name.localeCompare(b.name));
}

/** `4m 05s`, `1h 12m`, `12s`: a countdown as the crafting tab writes one. */
export function formatEta(ms: number): string {
  const s = Math.max(0, Math.round(ms / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${String(s % 60).padStart(2, '0')}s`;
  return `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, '0')}m`;
}
